import React, { useMemo, useCallback } from "react";
import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  GET_CURRENTLY_REGISTERED_VEHICLES,
  GetCurrentlyRegisteredVehiclesData,
  GetCurrentlyRegisteredVehiclesVars,
} from "common/graphql/queries/GetCurrentlyRegisteredVehicles";
import { usePermitApplicationContext } from "../PermitApplicationContext";
import { ReviewSubmitStepContent } from "./ReviewSubmitStepContent";
import { ReviewVehicle, ReviewDocument } from "./types";

/**
 * Container component for the Review & Submit step (Step 5).
 *
 * Responsibilities:
 * - Read state from PermitApplicationContext
 * - Fetch vehicle details for selected vehicles
 * - Transform context state into presentation props
 * - Handle terms acceptance callback
 */
export const ReviewSubmitStep: React.FC = () => {
  const { state, acceptTerms, clearTerms } = usePermitApplicationContext();

  const { authorityName, authorityId } = state.authority;
  const { permitType } = state.permitTypeSelection;
  const { selectedVehicleIds } = state.vehicleSelection;
  const { acceptedTermsAt, documents } = state;

  // Fetch vehicles data
  const { data: vehiclesData, loading: vehiclesLoading } = useQuery<
    GetCurrentlyRegisteredVehiclesData,
    GetCurrentlyRegisteredVehiclesVars
  >(GET_CURRENTLY_REGISTERED_VEHICLES, {
    variables: { limit: 1000 },
    skip: !permitType || permitType.vehicleScope === "per_account",
  });

  // Filter to only selected vehicles and transform to ReviewVehicle shape
  const vehicles: ReviewVehicle[] = useMemo(() => {
    if (!vehiclesData || permitType?.vehicleScope === "per_account") {
      return [];
    }

    return vehiclesData.registeredVehicles
      .filter((v) => selectedVehicleIds.includes(v.id))
      .map((v) => ({
        id: v.id,
        licensePlate: v.licensePlate,
        stateCode: v.stateCode,
        make: v.make,
        model: v.model,
        year: v.year,
      }));
  }, [vehiclesData, selectedVehicleIds, permitType?.vehicleScope]);

  // Transform documents state to ReviewDocument shape
  const reviewDocuments: ReviewDocument[] = useMemo(() => {
    return documents.map((doc) => ({
      documentTypeId: doc.documentTypeId,
      documentTypeName: doc.documentTypeName,
      documentId: doc.documentId,
    }));
  }, [documents]);

  // Handle terms change - call acceptTerms when checked, clearTerms when unchecked
  const handleTermsChange = useCallback(
    (acceptedAt: string | null) => {
      if (acceptedAt) {
        acceptTerms();
      } else {
        clearTerms();
      }
    },
    [acceptTerms, clearTerms],
  );

  // Guard: Ensure authority is selected
  if (!authorityName || !authorityId) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Authority Selected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please go back and select an authority first.
        </Typography>
      </Box>
    );
  }

  // Guard: Ensure permit type is selected
  if (!permitType) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Permit Type Selected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please go back and select a permit type first.
        </Typography>
      </Box>
    );
  }

  return (
    <ReviewSubmitStepContent
      authorityName={authorityName}
      authorityId={authorityId}
      permitType={permitType}
      vehicles={vehicles}
      vehiclesLoading={vehiclesLoading}
      documents={reviewDocuments}
      termsAcceptedAt={acceptedTermsAt}
      onTermsChange={handleTermsChange}
    />
  );
};

export default ReviewSubmitStep;
